import type { z } from "zod";
import type { DealProduct, DealProductWithLocation } from "../deal.types";
import { dealResponseSchema } from "../schemas/deal.schema";
import { rawDealProductSchema } from "../schemas/deal-product.schema";
import { locationsResponseSchema } from "../schemas/install-location.schema";
import { productFeaturesSchema } from "../schemas/product-features.schema";

export function parseLocationMap(raw: unknown): Record<string, string> {
    const result = locationsResponseSchema.safeParse(raw);
    if (!result.success) return {};
    return Object.fromEntries(
        result.data.InstallLocations.docs.map((l) => [l.id, l.name]),
    );
}

export function parseDealResponse(raw: unknown): {
    id: number | null;
    products: z.infer<typeof rawDealProductSchema>[];
} {
    const result = dealResponseSchema.safeParse(raw);
    if (!result.success) return { id: null, products: [] };
    return {
        id: result.data.getDeal.id,
        products: result.data.getDeal.products,
    };
}

export function parseDealProduct(raw: unknown): DealProduct {
    const product = rawDealProductSchema.parse(raw);
    const { width, height, outerColorId } = productFeaturesSchema.parse(
        product.features,
    );
    return {
        id: product.id,
        productId: product.productId,
        installLocationId: product.installLocationId,
        quantity: product.quantity,
        width,
        height,
        outerColorId,
        previewImage: product.previewImage,
    };
}

export function parseDealProductWithLocation(
    raw: unknown,
    locationMap: Record<string, string>,
    fallbackLocationName: string,
): DealProductWithLocation {
    const product = parseDealProduct(raw);
    return {
        ...product,
        locationName:
            locationMap[product.installLocationId ?? ""] ??
            fallbackLocationName,
    };
}
